import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "@/api";
import { useI18n } from "@/i18n";
import { useAuth, can } from "@/auth";
import { PageHeader, Tile, Loading, StatusPill, OperatorLink } from "@/components/Bits";
import { PencilSimple, Trash, Plus } from "@phosphor-icons/react";

const STATUSES = ["Open", "In Progress", "Done", "Cancelled"];
const PRIORITIES = ["High", "Medium", "Low"];
const TYPES = ["Site visit", "Meeting", "Call", "Email", "Other"];

const EMPTY = { date: "", city: "", operator: "", contact: "", type: "Meeting", subject: "", summary: "", next_action: "", due_date: "", status: "Open", priority: "Medium", linked_opp_id: "" };

export default function Visits() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const editable = can(user, "sales");

  const load = () => api.get("/visits").then((r) => setRows(r.data.rows));
  useEffect(() => { load(); }, []);

  // Pre-fill from notification quick action — opens the new-visit modal
  // automatically with values from ?operator=…&city=…&notes=…
  useEffect(() => {
    if (!editable) return;
    const op = params.get("operator");
    const city = params.get("city");
    const notes = params.get("notes");
    if (op || city || notes) {
      setForm({
        ...EMPTY,
        date: new Date().toISOString().slice(0, 10),
        operator: op || "",
        city: city || "",
        subject: notes || "",
        summary: notes || "",
      });
      setEditing({});
      // Strip params from URL so a refresh doesn't reopen the modal
      navigate("/visits", { replace: true });
    }
  }, [params, editable, navigate]);

  const openNew = () => { setForm(EMPTY); setEditing({}); };
  const openEdit = (r) => { setForm({ ...EMPTY, ...r }); setEditing(r); };
  const close = () => { setEditing(null); setForm(EMPTY); };

  const save = async () => {
    if (editing && editing.id) {
      await api.put(`/visits/${editing.id}`, form);
    } else {
      await api.post("/visits", form);
    }
    close();
    await load();
  };

  const del = async (id) => {
    if (!confirm(lang === "fr" ? "Supprimer cette visite ?" : "Delete this visit?")) return;
    await api.delete(`/visits/${id}`);
    await load();
  };

  if (!rows) return <Loading />;

  return (
    <div data-testid="visits-page">
      <PageHeader
        title={t("visits.title")}
        subtitle={`${rows.length} entries`}
        right={
          editable && (
            <button onClick={openNew} className="btn-accent flex items-center gap-2" data-testid="visit-new">
              <Plus size={14} weight="bold" /> {t("visits.newBtn")}
            </button>
          )
        }
      />
      <div className="p-4 md:p-8">
        <Tile>
          <div className="overflow-x-auto">
          <table className="tech" data-testid="visits-table">
            <thead>
              <tr>
                <th>{t("visits.date")}</th>
                <th>{t("table.city")}</th>
                <th>{t("table.operator")}</th>
                <th>{t("visits.subject")}</th>
                <th>{t("visits.nextAction")}</th>
                <th>{t("visits.due")}</th>
                <th>{t("visits.priority")}</th>
                <th>{t("visits.status")}</th>
                <th>{t("visits.linkedOpp")}</th>
                {editable && <th />}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td className="font-mono text-xs">{(r.date || "").slice(0, 10)}</td>
                  <td>{r.city}</td>
                  <td><OperatorLink name={r.operator} /></td>
                  <td className="max-w-xs">{r.subject}</td>
                  <td className="max-w-xs text-zinc-400 text-xs">{r.next_action}</td>
                  <td className="font-mono text-xs">{(r.due_date || "").slice(0, 10)}</td>
                  <td>{r.priority}</td>
                  <td><StatusPill status={r.status} /></td>
                  <td className="font-mono text-xs text-zinc-500">{r.linked_opp_id}</td>
                  {editable && (
                    <td className="text-right">
                      <button onClick={() => openEdit(r)} className="text-zinc-400 hover:text-white p-1" data-testid={`edit-${r.id}`}>
                        <PencilSimple size={14} />
                      </button>
                      <button onClick={() => del(r.id)} className="text-zinc-400 hover:text-wabtec-red p-1" data-testid={`delete-${r.id}`}>
                        <Trash size={14} />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </Tile>
      </div>

      {editing !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.4)", backdropFilter: "blur(8px)" }} onClick={close}>
          <div className="tile p-5 md:p-7 w-full max-w-2xl fade-in" onClick={(e) => e.stopPropagation()} data-testid="visit-modal" style={{ boxShadow: "var(--shadow-lg)" }}>
            <h3 className="text-[20px] font-semibold mb-5 tracking-tight">
              {editing.id
                ? (lang === "fr" ? "Modifier la visite" : "Edit visit")
                : (lang === "fr" ? "Nouvelle visite" : "New visit")}
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <Field label={t("visits.date")} type="date" v={form.date} set={(v) => setForm({ ...form, date: v })} />
              <Field label={t("visits.due")} type="date" v={form.due_date} set={(v) => setForm({ ...form, due_date: v })} />
              <Field label={t("table.city")} v={form.city} set={(v) => setForm({ ...form, city: v })} />
              <Field label={t("table.operator")} v={form.operator} set={(v) => setForm({ ...form, operator: v })} />
              <Field label="Contact" v={form.contact} set={(v) => setForm({ ...form, contact: v })} />
              <FieldSel label="Type" v={form.type} set={(v) => setForm({ ...form, type: v })} opts={TYPES} />
              <Field label={t("visits.subject")} v={form.subject} set={(v) => setForm({ ...form, subject: v })} colspan />
              <Field label={t("visits.summary")} v={form.summary} set={(v) => setForm({ ...form, summary: v })} colspan ta />
              <Field label={t("visits.nextAction")} v={form.next_action} set={(v) => setForm({ ...form, next_action: v })} colspan />
              <FieldSel label={t("visits.priority")} v={form.priority} set={(v) => setForm({ ...form, priority: v })} opts={PRIORITIES} />
              <FieldSel label={t("visits.status")} v={form.status} set={(v) => setForm({ ...form, status: v })} opts={STATUSES} />
              <Field label={t("visits.linkedOpp")} v={form.linked_opp_id} set={(v) => setForm({ ...form, linked_opp_id: v })} />
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={close} className="btn-ghost" data-testid="visit-cancel">{t("visits.cancel")}</button>
              <button onClick={save} className="btn-primary" data-testid="visit-save">{t("visits.save")}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, v, set, type = "text", colspan, ta }) {
  return (
    <div className={colspan ? "col-span-2" : ""}>
      <label className="label-tech block mb-1">{label}</label>
      {ta ? (
        <textarea className="input-tech" rows={2} value={v || ""} onChange={(e) => set(e.target.value)} />
      ) : (
        <input type={type} className="input-tech" value={v || ""} onChange={(e) => set(e.target.value)} />
      )}
    </div>
  );
}
function FieldSel({ label, v, set, opts }) {
  return (
    <div>
      <label className="label-tech block mb-1">{label}</label>
      <select className="input-tech" value={v || ""} onChange={(e) => set(e.target.value)}>
        {opts.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}
